import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { AgreementContextData, PurchaserType, Director, IndividualDetails, Preferences, Signature } from '../types';
import { deleteIdFile } from '../services/agreementService';

interface AgreementState extends AgreementContextData {
  furthestStep: number;
}

type AgreementAction =
  | { type: 'SET_PURCHASER_TYPE'; payload: PurchaserType }
  | { type: 'SET_PARTY_SIZE'; payload: number }
  | { type: 'SET_PREFERENCES'; payload: Preferences }
  | { type: 'SET_INDIVIDUAL_DETAILS'; payload: IndividualDetails[] }
  | { type: 'ADD_DIRECTOR'; payload: { type: 'smsf' | 'trust' | 'company'; director: Director } }
  | { type: 'REMOVE_DIRECTOR'; payload: { type: 'smsf' | 'trust' | 'company'; directorId: string } }
  | { type: 'UPDATE_DIRECTOR'; payload: { type: 'smsf' | 'trust' | 'company'; director: Director } }
  | { type: 'SET_SMSF_DETAILS'; payload: { smsfName: string; corporateTrustee: string } }
  | { type: 'SET_TRUST_DETAILS'; payload: { trustName: string; corporateTrustee: string } }
  | { type: 'SET_COMPANY_DETAILS'; payload: { trustName: string; corporateTrustee: string } }
  | { type: 'SET_SMSF_DETAILS_WITH_DIRECTORS'; payload: { smsfName: string; corporateTrustee: string; directors: Director[] } }
  | { type: 'SET_TRUST_DETAILS_WITH_DIRECTORS'; payload: { trustName: string; corporateTrustee: string; directors: Director[] } }
  | { type: 'SET_COMPANY_DETAILS_WITH_DIRECTORS'; payload: { trustName: string; corporateTrustee: string; directors: Director[] } }
  | { type: 'ADD_SIGNATURE'; payload: Signature }
  | { type: 'REMOVE_SIGNATURE'; payload: string }
  | { type: 'UPDATE_INDIVIDUAL_SIGNATURE'; payload: { individualId: string; signatureFile: Array<{ id_url: string; id_type: string; sig_row_id?: string }> } }
  | { type: 'SET_CURRENT_STEP'; payload: number }
  | { type: 'UPDATE_FURTHEST_STEP'; payload: number }
  | { type: 'RESET_AGREEMENT' }
  | { type: 'LOAD_AGREEMENT'; payload: AgreementContextData };

// Utility function to delete ID files for individuals/directors
const deleteIdFilesForIndividuals = async (agreementId: string | undefined, individuals: (IndividualDetails | Director)[]) => {
  if (!agreementId) return;
  
  console.log("AgreementContext - Deleting ID files for individuals:", individuals);
  
  // Filter individuals who have ID files
  // Use type assertion to check for id_file property
  const individualsWithIdFiles = individuals.filter(individual => {
    // Check if the individual has any ID file related properties
    return (
      (individual as any).id_file !== undefined ||
      (individual as any).id_file_data !== undefined ||
      (individual as any).id_file_url !== undefined
    );
  });
  
  if (individualsWithIdFiles.length === 0) {
    console.log("AgreementContext - No ID files to delete");
    return;
  }
  
  // Delete ID files for each individual
  for (const individual of individualsWithIdFiles) {
    if (!individual.id) {
      console.log("AgreementContext - Individual has no ID, skipping delete");
      continue;
    }
    
    try {
      console.log(`AgreementContext - Deleting ID file for individual: ${individual.id}`);
      await deleteIdFile(agreementId, individual.id);
      console.log(`AgreementContext - Successfully deleted ID file for individual: ${individual.id}`);
    } catch (error) {
      console.error(`AgreementContext - Error deleting ID file for individual ${individual.id}:`, error);
    }
  }
};

const initialState: AgreementState = {
  purchaserType: 'personal',
  partySize: 1,
  preferences: {
    maxPrice: 1000000,
    propertyPreferences: ''
  },
  individualDetails: [],
  signatures: [],
  currentStep: 1,
  furthestStep: 1,
  isComplete: false
};

function agreementReducer(state: AgreementState, action: AgreementAction): AgreementState {
  console.log("AgreementContext - Reducer called with action:", action.type);
  
  switch (action.type) {
    case 'SET_PURCHASER_TYPE':
      console.log("AgreementContext - SET_PURCHASER_TYPE action received:", action.payload);
      console.log("AgreementContext - Current purchaserType:", state.purchaserType);
      console.log("AgreementContext - New purchaserType:", action.payload);
      console.log("AgreementContext - typeof new purchaserType:", typeof action.payload);
      
      // If the purchaser type hasn't changed, just return the current state
      if (state.purchaserType === action.payload) {
        console.log("AgreementContext - Purchaser type hasn't changed, preserving existing data");
        return state;
      }
      
      // When purchaser type changes, we need to reset all relevant data
      // to avoid incorrect prefilled values
      const defaultPreferences = {
        maxPrice: 1000000,
        propertyPreferences: ''
      };
      
      // Delete ID files for individuals/directors before resetting state
      // Collect all individuals/directors who might have ID files
      const allIndividuals: (IndividualDetails | Director)[] = [];
      
      // Add individuals if they exist
      if (state.individualDetails && state.individualDetails.length > 0) {
        allIndividuals.push(...state.individualDetails);
      }
      
      // Add directors from entity-specific details if they exist
      if (state.purchaserType === 'smsf' && state.smsfDetails?.directors) {
        allIndividuals.push(...state.smsfDetails.directors);
      } else if (state.purchaserType === 'trust' && state.trustDetails?.directors) {
        allIndividuals.push(...state.trustDetails.directors);
      } else if (state.purchaserType === 'company' && state.companyDetails?.directors) {
        allIndividuals.push(...state.companyDetails.directors);
      }
      
      // Delete ID files for all individuals/directors
      // We need to use setTimeout because we can't use async/await directly in the reducer
      if (allIndividuals.length > 0) {
        console.log("AgreementContext - Scheduling deletion of ID files for individuals:", allIndividuals);
        setTimeout(() => {
          // Extract agreement ID from state if available
          const agreementId = (state as any).id;
          deleteIdFilesForIndividuals(agreementId, allIndividuals)
            .then(() => console.log("AgreementContext - Successfully deleted ID files"))
            .catch(error => console.error("AgreementContext - Error deleting ID files:", error));
        }, 0);
      }
      
      // Reset to default values based on the new purchaser type
      let updatedState = {
        ...state,
        purchaserType: action.payload,
        preferences: defaultPreferences,
        individualDetails: action.payload === 'personal' ? [{
          id: "1",
          firstName: '',
          middleName: '',
          lastName: '',
          email: '',
          phone: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          country: 'Australia',
          state: '',
          postcode: ''
        }] : [],
        partySize: action.payload === 'personal' ? 1 : 1
      };
      
      // Reset entity-specific details based on the new purchaser type
      if (action.payload === 'smsf') {
        updatedState.smsfDetails = {
          smsfName: '',
          corporateTrustee: '',
          directors: []
        };
      } else if (action.payload === 'trust') {
        updatedState.trustDetails = {
          trustName: '',
          corporateTrustee: '',
          directors: []
        };
      } else if (action.payload === 'company') {
        updatedState.companyDetails = {
          trustName: '',
          corporateTrustee: '',
          directors: []
        };
      }
      
      console.log("AgreementContext - Resetting all relevant data due to purchaser type change");
      console.log("AgreementContext - New state after SET_PURCHASER_TYPE:", updatedState);
      return updatedState;
    
    case 'SET_PARTY_SIZE':
      return {
        ...state,
        partySize: action.payload,
        individualDetails: Array(action.payload).fill(null).map((_, i) =>
          (state.individualDetails && state.individualDetails[i]) || {
            id: Date.now().toString() + i,
            role: 'individual',
            addedAt: new Date(),
            firstName: '',
            middleName: '',
            lastName: '',
            email: '',
            phone: '',
            addressLine1: '',
            addressLine2: '',
            city: '',
            country: 'Australia',
            state: '',
            postcode: ''
          }
        )
      };
    
    case 'SET_PREFERENCES':
      console.log("AgreementContext - SET_PREFERENCES action received:", action.payload);
      console.log("AgreementContext - Current state preferences:", state.preferences);
      
      // Check if preferences are actually changing
      const preferencesChanged = !state.preferences ||
        state.preferences.maxPrice !== action.payload.maxPrice ||
        state.preferences.propertyPreferences !== action.payload.propertyPreferences;
      
      if (!preferencesChanged) {
        console.log("AgreementContext - Preferences haven't changed, preserving existing state");
        return state;
      }
      
      const newState = { ...state, preferences: action.payload };
      console.log("AgreementContext - New state preferences:", newState.preferences);
      return newState;
    
    case 'SET_INDIVIDUAL_DETAILS':
      return { ...state, individualDetails: action.payload };
    
    case 'ADD_DIRECTOR':
      if (action.payload.type === 'smsf') {
        return {
          ...state,
          smsfDetails: {
            ...state.smsfDetails!,
            directors: [...(state.smsfDetails?.directors || []), action.payload.director]
          }
        };
      } else if (action.payload.type === 'trust') {
        return {
          ...state,
          trustDetails: {
            ...state.trustDetails!,
            directors: [...(state.trustDetails?.directors || []), action.payload.director]
          }
        };
      } else if (action.payload.type === 'company') {
        return {
          ...state,
          companyDetails: {
            ...state.companyDetails!,
            directors: [...(state.companyDetails?.directors || []), action.payload.director]
          }
        };
      }
      return state;
    
    case 'REMOVE_DIRECTOR':
      console.log("AgreementContext - REMOVE_DIRECTOR action received:", action.payload);
      
      // Find the director to be removed to delete their ID file
      let directorToRemove: Director | undefined;
      
      if (action.payload.type === 'smsf' && state.smsfDetails) {
        directorToRemove = state.smsfDetails.directors.find(d => d.id === action.payload.directorId);
      } else if (action.payload.type === 'trust' && state.trustDetails) {
        directorToRemove = state.trustDetails.directors.find(d => d.id === action.payload.directorId);
      } else if (action.payload.type === 'company' && state.companyDetails) {
        directorToRemove = state.companyDetails.directors.find(d => d.id === action.payload.directorId);
      }
      
      // Delete ID file for the removed director if it exists
      if (directorToRemove) {
        console.log("AgreementContext - Found director to remove:", directorToRemove);
        
        // Check if the director has an ID file
        const hasIdFile = (
          (directorToRemove as any).id_file !== undefined ||
          (directorToRemove as any).id_file_data !== undefined ||
          (directorToRemove as any).id_file_url !== undefined
        );
        
        if (hasIdFile) {
          console.log("AgreementContext - Director has ID file, scheduling deletion");
          
          // We need to use setTimeout because we can't use async/await directly in the reducer
          setTimeout(() => {
            // Extract agreement ID from state if available
            const agreementId = (state as any).id;
            if (agreementId && directorToRemove?.id) {
              deleteIdFile(agreementId, directorToRemove.id)
                .then(() => console.log(`AgreementContext - Successfully deleted ID file for director ${directorToRemove?.id}`))
                .catch(error => console.error(`AgreementContext - Error deleting ID file for director ${directorToRemove?.id}:`, error));
            } else {
              console.log("AgreementContext - Missing agreementId or directorId, cannot delete ID file");
            }
          }, 0);
        }
      }
      
      // Update state to remove the director
      if (action.payload.type === 'smsf' && state.smsfDetails) {
        return {
          ...state,
          smsfDetails: {
            ...state.smsfDetails,
            directors: state.smsfDetails.directors.filter(d => d.id !== action.payload.directorId)
          }
        };
      } else if (action.payload.type === 'trust' && state.trustDetails) {
        return {
          ...state,
          trustDetails: {
            ...state.trustDetails,
            directors: state.trustDetails.directors.filter(d => d.id !== action.payload.directorId)
          }
        };
      } else if (action.payload.type === 'company' && state.companyDetails) {
        return {
          ...state,
          companyDetails: {
            ...state.companyDetails,
            directors: state.companyDetails.directors.filter(d => d.id !== action.payload.directorId)
          }
        };
      }
      return state;
    
    case 'UPDATE_DIRECTOR':
      if (action.payload.type === 'smsf' && state.smsfDetails) {
        return {
          ...state,
          smsfDetails: {
            ...state.smsfDetails,
            directors: state.smsfDetails.directors.map(d => 
              d.id === action.payload.director.id ? action.payload.director : d
            )
          }
        };
      } else if (action.payload.type === 'trust' && state.trustDetails) {
        return {
          ...state,
          trustDetails: {
            ...state.trustDetails,
            directors: state.trustDetails.directors.map(d => 
              d.id === action.payload.director.id ? action.payload.director : d
            )
          }
        };
      } else if (action.payload.type === 'company' && state.companyDetails) {
        return {
          ...state,
          companyDetails: {
            ...state.companyDetails,
            directors: state.companyDetails.directors.map(d => 
              d.id === action.payload.director.id ? action.payload.director : d
            )
          }
        };
      }
      return state;
    
    case 'SET_SMSF_DETAILS':
      return {
        ...state,
        smsfDetails: {
          smsfName: action.payload.smsfName,
          corporateTrustee: action.payload.corporateTrustee,
          directors: state.smsfDetails?.directors || []
        }
      };
    
    case 'SET_TRUST_DETAILS':
      return {
        ...state,
        trustDetails: {
          trustName: action.payload.trustName,
          corporateTrustee: action.payload.corporateTrustee,
          directors: state.trustDetails?.directors || []
        }
      };
    
    case 'SET_COMPANY_DETAILS':
      return {
        ...state,
        companyDetails: {
          trustName: action.payload.trustName,
          corporateTrustee: action.payload.corporateTrustee,
          directors: state.companyDetails?.directors || []
        }
      };
    
    case 'SET_SMSF_DETAILS_WITH_DIRECTORS':
      return {
        ...state,
        smsfDetails: {
          smsfName: action.payload.smsfName,
          corporateTrustee: action.payload.corporateTrustee,
          directors: action.payload.directors
        }
      };
    
    case 'SET_TRUST_DETAILS_WITH_DIRECTORS':
      return {
        ...state,
        trustDetails: {
          trustName: action.payload.trustName,
          corporateTrustee: action.payload.corporateTrustee,
          directors: action.payload.directors
        }
      };
    
    case 'SET_COMPANY_DETAILS_WITH_DIRECTORS':
      return {
        ...state,
        companyDetails: {
          trustName: action.payload.trustName,
          corporateTrustee: action.payload.corporateTrustee,
          directors: action.payload.directors
        }
      };
    
    case 'ADD_SIGNATURE':
      return {
        ...state,
        signatures: [...(state.signatures || []), action.payload]
      };
    
    case 'REMOVE_SIGNATURE':
      return {
        ...state,
        signatures: (state.signatures || []).filter(s => s.id !== action.payload)
      };
    
    case 'UPDATE_INDIVIDUAL_SIGNATURE':
      console.log("AgreementContext - UPDATE_INDIVIDUAL_SIGNATURE action received:", action.payload);
      // Add additional logging to help with debugging
      console.log("AgreementContext - Current individual details:", state.individualDetails);
      return {
        ...state,
        individualDetails: state.individualDetails.map(individual => {
          if (individual.id === action.payload.individualId) {
            console.log(`AgreementContext - Updating individual ${individual.id} with signature file:`, action.payload.signatureFile);
            return { ...individual, sig_file: action.payload.signatureFile };
          }
          return individual;
        })
      };
    
    case 'SET_CURRENT_STEP':
      // Ensure the step is a valid number
      const newStep = (typeof action.payload === 'number' && !isNaN(action.payload)) ? action.payload : 1;
      console.log("AgreementContext - SET_CURRENT_STEP:", { payload: action.payload, newStep });
      return { ...state, currentStep: newStep };
    
    case 'UPDATE_FURTHEST_STEP':
      return { 
        ...state, 
        furthestStep: Math.max(state.furthestStep, action.payload)
      };
    
    case 'RESET_AGREEMENT':
      return initialState;
    
    case 'LOAD_AGREEMENT':
      console.log("AgreementContext - LOAD_AGREEMENT action received:", action.payload);
      console.log("AgreementContext - Current state before loading:", state);
      
      // Ensure we preserve the furthestStep property from the current state
      // or use a default value if it doesn't exist
      // Also ensure currentStep is a valid number and signatures is always an array
      const loadedState = {
        ...action.payload,
        furthestStep: state.furthestStep || 1,
        currentStep: (typeof action.payload.currentStep === 'number' && !isNaN(action.payload.currentStep))
          ? action.payload.currentStep
          : (typeof state.currentStep === 'number' && !isNaN(state.currentStep))
            ? state.currentStep
            : 1,
        signatures: Array.isArray(action.payload.signatures) ? action.payload.signatures : []
      };
      
      console.log("AgreementContext - Merged state after loading:", loadedState);
      return loadedState;
    
    default:
      return state;
  }
}

interface AgreementContextType {
  state: AgreementState;
  dispatch: React.Dispatch<AgreementAction>;
}

const AgreementContext = createContext<AgreementContextType | undefined>(undefined);

export function AgreementProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(agreementReducer, initialState);

  return (
    <AgreementContext.Provider value={{ state, dispatch }}>
      {children}
    </AgreementContext.Provider>
  );
}

export function useAgreement() {
  const context = useContext(AgreementContext);
  if (context === undefined) {
    throw new Error('useAgreement must be used within an AgreementProvider');
  }
  return context;
}